<template>
    <div class="stepper-container">
        <div class="stepper-progress">
            <div class="stepper-icon">
                <img src="/images/icons/side-nav/calendar-dark.svg" alt="stepper-icon" />
            </div>
            <div v-for="(step, indx) in steps" :key="step.title" class="step-wrap">
                <div
                    :class="{
                        step: true,
                        current: isCurrentStep(indx),
                        completed: isCompletedStep(indx),
                        incomplete: isIncompleteStep(indx)
                    }"
                >
                    <p class="step-title">
                        <b>STEP {{ indx + 1 }}:</b> {{ step.title }}
                    </p>
                </div>
            </div>
        </div>
        <div class="stepper-content">
            <component
                :is="steps[stepIndex].component"
                :defaultVals="prevData"
                v-bind="steps[stepIndex].props"
                @next="handleNext"
                @prev="handlePrev"
            >
            </component>
        </div>
    </div>
</template>

<script>
    /**
     * Stepper for multistep registrations
     * @displayName Stepper
     *
     * Each step implementations:
     * @event next Handles next step (required)
     *  @fires {object} data Step data from step
     * @event prev Handles prev step
     * defaultVals  Initializes step with data
     */
    export default {
        name: 'Stepper',
        props: {
            /**
             * Array of Step objects for the Stepper
             * @property {string} title The step title
             * @property {object} component The step component
             * @property {object=} props Optional props passed into component object
             */
            steps: {
                type: Array,
                required: true,
                validator: (val) =>
                    val.every(
                        (i) =>
                            typeof i === 'object' &&
                            Object.hasOwn(i, 'title') &&
                            Object.hasOwn(i, 'component')
                    )
            },

            /**
             * The index to start Stepper with
             */
            initStepIndx: {
                type: Number,
                required: false,
                default: 0
            }
        },

        data: () => ({
            stepIndex: 0,
            prevData: null,
            stepData: {}
        }),

        methods: {
            /**
             * When user clicks continue or next
             *
             * @param {object} data The step data passed by current step
             *
             */
            handleNext(data) {
                if (!data) return

                this.$set(this.stepData, this.steps[this.stepIndex].title, data)

                if (this.stepIndex < this.steps.length - 1) {
                    this.prevData = null
                    this.stepIndex++
                } else {
                    /**
                     * Final step event
                     *
                     * @event registrationComplete
                     * @type {object}
                     */
                    this.$emit('registrationComplete', this.stepData)
                }
            },

            /**
             * When user clicks back or prev
             */
            handlePrev() {
                if (this.stepIndex != 0) {
                    let prevStepData = this.stepData[this.steps[this.stepIndex - 1].title]

                    this.stepIndex--
                    this.prevData = prevStepData
                }
            }
        },

        computed: {
            isCurrentStep: (self) => (indx) => self.stepIndex === indx,
            isCompletedStep: (self) => (indx) => self.stepIndex > indx,
            isIncompleteStep: (self) => (indx) => self.stepIndex < indx
        },

        watch: {
            /**
             * Initializes Stepper with initStepIndx
             */
            initStepIndx: function (indx) {
                this.stepIndex = indx
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    .stepper-container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;

        .stepper-progress {
            order: 1;
            flex: 0 0 auto;
            display: flex;
            justify-content: left;
            margin-top: 0.5rem;

            .stepper-icon {
                position: relative;
                left: 15px;
                z-index: 10;
                border-radius: 50%;
                background-color: #cdeb09;
                width: 73px;
                height: 73px;
                text-align: center;

                img {
                    height: 30px;
                    position: relative;
                    top: 25%;
                }
            }

            .step-wrap {
                width: fit-content;
                margin-top: auto;
                margin-bottom: auto;
                margin-right: -14px;
                flex: 0 0 auto;

                // powerline effect
                clip-path: polygon(
                    0% 0%,
                    calc(100% - 14px) 0%,
                    100% 50%,
                    calc(100% - 14px) 100%,
                    100% 100%,
                    0% 100%,
                    14px 50%
                );
                background-color: black;
                border: 1px solid black;

                .step {
                    font-size: 0.75rem;
                    font-weight: 500;
                    position: relative;
                    padding: 3px 1.25rem;
                    cursor: pointer;
                    height: 30;
                    width: 318px;

                    // powerline effect
                    clip-path: polygon(
                        0% 0%,
                        calc(100% - 14px) 0%,
                        100% 50%,
                        calc(100% - 14px) 100%,
                        100% 100%,
                        0% 100%,
                        14px 50%
                    );

                    .step-title {
                        font-family: 'Instrument Sans';
                        font-style: normal;
                        font-weight: 500;
                        font-size: 18px;
                        line-height: 22px;
                        letter-spacing: 0.1em;
                        text-transform: uppercase;
                        margin-top: auto;
                        margin-bottom: auto;
                        padding-left: 30px;
                    }
                }

                .current {
                    background-color: #cdeb09;
                }

                .completed {
                    background-color: #3a3d40;
                    color: white;
                }

                .incomplete {
                    background-color: #a1a1aa;
                }
            }
        }
        .stepper-content {
            order: 2;
            flex: 0 0 auto;
            background: #262626;
            margin-top: 90px;
            margin-bottom: 90px;
            border-radius: 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            .stepper-action {
                margin-top: 20px;
                margin-bottom: 50px;

                button {
                    background-color: #e2ee79;
                    border-radius: 50px;
                    height: 62px;
                    width: 467px;
                    padding: 20px;
                    display: flex;
                    gap: 10px;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    font-family: 'Instrument Sans';
                    font-style: normal;
                    font-weight: 500;
                    font-size: 18px;
                    line-height: 22px;
                    text-align: center;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: #1b0d29;
                }
            }
        }
    }
</style>
